import React, { useEffect, useMemo, useState } from 'react';
import Drawer from '../ui/Drawer';
import {
  workflowService,
  type WorkflowRunDetail,
  type WorkflowRunEvent,
  type WorkflowRunSummary,
} from '../../services/workflowService';
import { Surface } from '../ui/Surface';

interface WorkflowRunDrawerProps {
  open: boolean;
  onClose: () => void;
  workflowId?: string;
  workflowName: string;
  runId?: string;
}

const runStatusClasses: Record<string, string> = {
  created: 'border-gray-200 bg-gray-50 text-gray-600',
  running: 'border-amber-200 bg-amber-50 text-amber-700',
  completed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  failed: 'border-red-200 bg-red-50 text-red-700',
};

const eventTypeClasses: Record<string, string> = {
  intent_resolved: 'bg-blue-50 text-blue-700',
  transition_selected: 'bg-violet-50 text-violet-700',
  terminal_transition_selected: 'bg-emerald-50 text-emerald-700',
  node_selected: 'bg-amber-50 text-amber-700',
  execution_failed: 'bg-red-50 text-red-700',
  graph_fallback: 'bg-gray-100 text-gray-700',
};

const formatLabel = (value: string) =>
  value
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const WorkflowRunDrawer: React.FC<WorkflowRunDrawerProps> = ({
  open,
  onClose,
  workflowId,
  workflowName,
  runId,
}) => {
  const [loading, setLoading] = useState(false);
  const [latestRun, setLatestRun] = useState<WorkflowRunSummary | null>(null);
  const [runDetail, setRunDetail] = useState<WorkflowRunDetail | null>(null);
  const [events, setEvents] = useState<WorkflowRunEvent[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (!workflowId) {
      setLatestRun(null);
      setRunDetail(null);
      setEvents([]);
      setError(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const latest = runId
          ? await workflowService.getRunById(runId)
          : (await workflowService.listRuns(workflowId, { limit: 1 })).items?.[0] ?? null;
        if (cancelled) return;
        setLatestRun(latest);

        if (!latest) {
          setRunDetail(null);
          setEvents([]);
          return;
        }

        const [detail, eventItems] = await Promise.all([workflowService.getRunById(latest.id), workflowService.getRunEvents(latest.id)]);
        if (cancelled) return;
        setRunDetail(detail);
        setEvents(eventItems);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load workflow run');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    const interval = window.setInterval(load, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [open, workflowId, runId]);

  const progressPercent = useMemo(() => {
    if (!runDetail?.steps.length) return 0;
    const completedSteps = runDetail.steps.filter((step) => step.status === 'completed').length;
    return Math.round((completedSteps / runDetail.steps.length) * 100);
  }, [runDetail]);

  return (
    <Drawer open={open} onClose={onClose} title={`Run: ${workflowName}`} widthClassName="w-full max-w-2xl">
      <div className="space-y-5">
        {!workflowId ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Save this workflow first. Real run data is only available for persisted workflows.
          </div>
        ) : null}

        {loading ? (
          <Surface className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
            Loading latest workflow run...
          </Surface>
        ) : null}

        {!loading && error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {!loading && workflowId && !latestRun && !error ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            No real runs found for this workflow yet.
          </div>
        ) : null}

        {!loading && latestRun ? (
          <>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">Latest Run</div>
                  <div className="mt-1 text-xs text-gray-500">
                    Started {new Date(latestRun.startedAt).toLocaleString()}
                  </div>
                </div>
                <span
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                    runStatusClasses[latestRun.status] ?? 'border-gray-200 bg-gray-50 text-gray-600'
                  }`}
                >
                  {latestRun.status}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 text-sm text-gray-600 md:grid-cols-2">
                <div>Channel: <span className="font-medium text-gray-900">{latestRun.channel}</span></div>
                <div>Entry: <span className="font-medium text-gray-900">{latestRun.entryType ?? 'n/a'}</span></div>
                <div>Intent: <span className="font-medium text-gray-900">{latestRun.resolvedIntent ?? 'n/a'}</span></div>
                <div>Capability: <span className="font-medium text-gray-900">{latestRun.resolvedCapabilityCode ?? 'n/a'}</span></div>
              </div>

              {runDetail?.steps.length ? (
                <div className="mt-4">
                  <div className="h-2 rounded-full bg-gray-200">
                    <div
                      className="h-2 rounded-full bg-blue-600 transition-all"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <div className="mt-2 text-right text-xs text-gray-500">{progressPercent}%</div>
                </div>
              ) : null}
            </div>

            {runDetail?.steps.length ? (
              <div className="space-y-3">
                <div className="text-sm font-semibold text-gray-900">Run Steps</div>
                {runDetail.steps.map((step) => (
                  <Surface tone="card" key={step.id} className="rounded-xl border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{step.nodeId}</div>
                        <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-gray-400">
                          {step.nodeKind}
                        </div>
                      </div>
                      <span
                        className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                          runStatusClasses[step.status] ?? 'border-gray-200 bg-gray-50 text-gray-600'
                        }`}
                      >
                        {step.status}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Started {new Date(step.startedAt).toLocaleString()}
                      {step.completedAt ? ` • Completed ${new Date(step.completedAt).toLocaleString()}` : ''}
                    </div>
                    {step.output?.error ? (
                      <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                        {String(step.output.error)}
                      </div>
                    ) : null}
                  </Surface>
                ))}
              </div>
            ) : null}

            <div className="space-y-3">
              <div className="text-sm font-semibold text-gray-900">Run Events</div>
              {events.length === 0 ? (
                <Surface className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-500">
                  No events recorded yet.
                </Surface>
              ) : (
                events.map((event) => (
                  <Surface tone="card" key={event.id} className="rounded-xl border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={`rounded-full px-2 py-1 text-[10px] font-medium uppercase tracking-wide ${
                            eventTypeClasses[event.type] ?? 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {formatLabel(event.type)}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(event.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <pre className="mt-3 overflow-auto rounded-lg bg-gray-50 p-3 text-xs text-gray-700">
                      {JSON.stringify(event.payload, null, 2)}
                    </pre>
                  </Surface>
                ))
              )}
            </div>
          </>
        ) : null}
      </div>
    </Drawer>
  );
};

export default WorkflowRunDrawer;
